import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Loader2, Trash2, Plus, Edit2 } from 'lucide-react'
import { companyAPI } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { toast } from '../../hooks/use-toast'
import ResizableCropModal from '../common/ResizableCropModal'
import { validateURL, validateTextLength, normalizeURL } from '../../utils/validation'

export default function CompanyDetailsModal({ open, onClose }) {
  const { user, updateUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [isUpdateMode, setIsUpdateMode] = useState(false)
  const [existingPhotos, setExistingPhotos] = useState({})
  const [companyName, setCompanyName] = useState('')
  const [companyDetails, setCompanyDetails] = useState({
    companyWebsite: '',
    companyDescription: '',
    companyLogo: null, // Keep for single logo backward compatibility
    companyLogos: [] // New: Array of logo objects
  })
  const [errors, setErrors] = useState({})
  const [logoVariants, setLogoVariants] = useState([]) // Track multiple logo variants
  const [deletedLogos, setDeletedLogos] = useState([]) // Track URLs of deleted logos

  // Cropping states for company logo
  const [logoCropModalOpen, setLogoCropModalOpen] = useState(false)
  const [logoTempImageSrc, setLogoTempImageSrc] = useState(null)
  const [currentLogoIndex, setCurrentLogoIndex] = useState(null) // Track which logo is being cropped

  // Fetch existing company data when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingData()
    }
  }, [open])

  const fetchExistingData = async () => {
    setFetchingData(true)
    try {
      const response = await companyAPI.getCompanyInfo()
      if (response.data.success && response.data.company) {
        const company = response.data.company

        // Store company name for display
        setCompanyName(company.companyName || '')

        // Always pre-populate form with existing data (regardless of intake status)
        setCompanyDetails({
          companyWebsite: company.website || '',
          companyDescription: company.description || '',
          companyLogo: null, // Files can't be pre-populated
          companyLogos: []
        })

        // Always handle logos if they exist (moved OUTSIDE intakeCompleted check)
        if (company.logos && company.logos.length > 0) {
          setLogoVariants(company.logos.map(logo => ({
            ...logo,
            isExisting: true,
            fileType: logo.fileType,
            fileName: logo.fileName
          })))
        } else if (company.logoPath) {
          // Convert legacy single logo to new format for display
          setLogoVariants([{
            url: company.logoPath,
            isExisting: true
          }])
          // Also keep in existingPhotos for backward compatibility
          setExistingPhotos({
            logoPath: company.logoPath
          })
        }

        // Check if intake is already completed (only for update mode flag)
        if (company.intakeCompleted) {
          setIsUpdateMode(true)
        }
      }
    } catch (error) {
      // It's ok if no data exists yet - this is expected for new companies
      console.log('No existing company data found')
    } finally {
      setFetchingData(false)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate company website
    const websiteValidation = validateURL(companyDetails.companyWebsite)
    if (!websiteValidation.isValid) {
      newErrors.companyWebsite = websiteValidation.error
    }

    // Validate company description
    const descriptionValidation = validateTextLength(
      companyDetails.companyDescription,
      50,
      150,
      'Company description'
    )
    if (!descriptionValidation.isValid) {
      newErrors.companyDescription = descriptionValidation.error
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle company logo selection (supports multiple)
  const handleLogoSelect = (e, index = null) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
      if (file.size > maxSize) {
        toast.error('File size exceeds 10MB limit. Please choose a smaller file.')
        e.target.value = '' // Clear the input
        return
      }

      // Validate file size (100KB min) - only for image files
      const minSize = 100 * 1024 // 100KB in bytes
      const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
      const isImageFile = imageTypes.includes(file.type.toLowerCase())

      if (isImageFile && file.size < minSize) {
        toast.error('File size must be at least 100KB. Please choose a higher resolution image.')
        e.target.value = '' // Clear the input
        return
      }

      // Check if file is an image type that needs cropping
      const needsCropping = imageTypes.includes(file.type.toLowerCase())

      if (needsCropping) {
        // For image files, open crop modal
        const reader = new FileReader()
        reader.onload = () => {
          setLogoTempImageSrc(reader.result)
          setCurrentLogoIndex(index !== null ? index : logoVariants.length)
          setLogoCropModalOpen(true)
        }
        reader.readAsDataURL(file)
      } else {
        // For non-image files (AI, SVG, EPS, PDF, ZIP), add directly without cropping
        const newLogo = {
          file: file,
          url: URL.createObjectURL(file),
          name: `Logo ${index !== null ? index + 1 : logoVariants.length + 1}`,
          isExisting: false,
          fileType: file.type,
          fileName: file.name
        }

        if (index !== null && index < logoVariants.length) {
          // Replacing existing logo
          const updatedVariants = [...logoVariants]
          updatedVariants[index] = newLogo
          setLogoVariants(updatedVariants)
        } else {
          // Adding new logo
          setLogoVariants([...logoVariants, newLogo])
        }

        e.target.value = '' // Clear the input for next selection
      }
    }
  }

  const handleLogoCropComplete = (croppedImage) => {
    const newLogo = {
      file: croppedImage.file,
      url: croppedImage.url,
      fileName: croppedImage.file.name,
      fileType: croppedImage.file.type,
      size: croppedImage.file.size,
      isExisting: false
    }

    if (currentLogoIndex !== null && currentLogoIndex < logoVariants.length) {
      // Replacing existing logo
      const oldLogo = logoVariants[currentLogoIndex]

      // If replacing an existing logo from database, track it for deletion
      if (oldLogo.isExisting && oldLogo.url) {
        setDeletedLogos([...deletedLogos, oldLogo.url])
      }

      const updatedVariants = [...logoVariants]
      updatedVariants[currentLogoIndex] = newLogo
      setLogoVariants(updatedVariants)
    } else {
      // Adding new logo
      setLogoVariants([...logoVariants, newLogo])
    }

    setLogoCropModalOpen(false)
    setLogoTempImageSrc(null)
    setCurrentLogoIndex(null)
  }

  const handleRemoveLogo = (index) => {
    const logoToRemove = logoVariants[index]

    // If it's an existing logo (from database), track it for deletion from Firebase
    if (logoToRemove.isExisting && logoToRemove.url) {
      setDeletedLogos([...deletedLogos, logoToRemove.url])
    }

    const updatedVariants = logoVariants.filter((_, i) => i !== index)
    setLogoVariants(updatedVariants)
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      setLoadingMessage('Preparing your data...')

      const formData = new FormData()
      formData.append('companyWebsite', companyDetails.companyWebsite)
      formData.append('companyDescription', companyDetails.companyDescription)

      // Handle multiple logos
      const newLogos = logoVariants.filter(logo => !logo.isExisting && logo.file)
      const existingLogos = logoVariants.filter(logo => logo.isExisting)

      // Send deleted logos for Firebase cleanup
      if (deletedLogos.length > 0) {
        formData.append('deletedLogos', JSON.stringify(deletedLogos))
      }

      // Always send logo information if there are any logos
      if (logoVariants.length > 0) {
        // Send existing logos information to preserve them
        if (existingLogos.length > 0) {
          formData.append('existingLogos', JSON.stringify(existingLogos.map(logo => ({
            url: logo.url,
            size: logo.size,
            fileType: logo.fileType,
            fileName: logo.fileName
          }))))
        }

        // Check if there are any new logos to upload
        if (newLogos.length > 0) {
          // Append each new logo file
          newLogos.forEach(logo => {
            formData.append('companyLogos', logo.file)
          })
        }
      } else if (logoVariants.length === 0 && isUpdateMode) {
        // All logos deleted
        formData.append('deleteLogos', 'true')
      }

      setLoadingMessage('Uploading files to cloud storage...')

      const response = await companyAPI.completeIntake(formData)
      if (response.data.success) {
        setLoadingMessage('Profile completed successfully!')

        updateUser({
          ...user,
          companyId: response.data.company._id,
          intakeCompleted: true
        })

        setTimeout(() => {
          setLoading(false)
          setLoadingMessage('')
          toast.success('Company profile completed successfully!')
          onClose()
        }, 1500)
      }
    } catch (error) {
      console.error('Error submitting company details:', error)
      setLoading(false)
      setLoadingMessage('')
      toast.error(error.response?.data?.message || 'Failed to complete company profile')
    }
  }

  const handleClose = () => {
    if (!loading) {
      setCompanyDetails({
        companyWebsite: '',
        companyDescription: '',
        companyLogo: null,
        companyLogos: []
      })
      setErrors({})
      setLogoTempImageSrc(null)
      setLogoVariants([])
      setDeletedLogos([])
      setCurrentLogoIndex(null)
      onClose()
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="w-full max-w-full sm:max-w-4xl max-h-[90vh] overflow-hidden mx-2 sm:mx-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-lg sm:text-xl">Company Details & Logo</DialogTitle>
            <DialogDescription>
              {isUpdateMode
                ? 'Update your company profile and branding assets'
                : 'Complete your company profile to unlock all platform features'
              }
            </DialogDescription>
          </DialogHeader>
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-1">
          {fetchingData ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading existing data...</span>
            </div>
          ) : (
            <div className="space-y-8 py-4">
              {/* Company Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-primary border-b pb-2">Company Information</h3>
                <div className="space-y-4">
                  {companyName && (
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <Label className="text-sm font-medium text-muted-foreground">Company Name</Label>
                      <p className="text-lg font-semibold mt-1">{companyName}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="companyWebsite" className="text-sm font-medium">
                      Company Website *
                    </Label>
                    <Input
                      id="companyWebsite"
                      type="url"
                      placeholder="https://www.company.com"
                      value={companyDetails.companyWebsite}
                      onChange={(e) => {
                        setCompanyDetails({ ...companyDetails, companyWebsite: e.target.value })
                        // Clear error when user types
                        if (errors.companyWebsite) {
                          setErrors({ ...errors, companyWebsite: '' })
                        }
                      }}
                      onBlur={(e) => {
                        // Normalize URL (add https:// if missing) and validate
                        const normalizedURL = normalizeURL(e.target.value)
                        if (normalizedURL) {
                          setCompanyDetails({ ...companyDetails, companyWebsite: normalizedURL })
                          const validation = validateURL(normalizedURL)
                          if (!validation.isValid) {
                            setErrors({ ...errors, companyWebsite: validation.error })
                          }
                        }
                      }}
                      autoComplete="off"
                      className={`h-11 ${errors.companyWebsite ? 'border-red-500' : ''}`}
                    />
                    {errors.companyWebsite && (
                      <p className="text-sm text-red-500">{errors.companyWebsite}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Example: https://www.yourcompany.com
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyDescription" className="text-sm font-medium">
                      Company Description * <span className="text-xs text-muted-foreground">(will be used for social media, press release etc.)</span>
                    </Label>
                    <Textarea
                      id="companyDescription"
                      placeholder="Brief description of your company..."
                      rows={3}
                      maxLength={150}
                      value={companyDetails.companyDescription}
                      onChange={(e) => {
                        setCompanyDetails({ ...companyDetails, companyDescription: e.target.value })
                        // Clear error when user types
                        if (errors.companyDescription) {
                          setErrors({ ...errors, companyDescription: '' })
                        }
                      }}
                      className={errors.companyDescription ? 'border-red-500' : ''}
                    />
                    <div className="flex justify-between items-center">
                      <span className={`text-xs ${errors.companyDescription ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {errors.companyDescription || `${companyDetails.companyDescription.length}/150 characters (minimum 50)`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>


              {/* Company Logo Section - Multiple Logo Support */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold text-primary">Company Logos</h3>
                  <span className="text-sm text-muted-foreground">
                    {logoVariants.length}/5 logos uploaded
                  </span>
                </div>
                <div className="space-y-4">
                  <Label className="text-sm font-medium">
                    Upload Logo Variants * <span className="text-xs text-muted-foreground">(max 5 logos)</span> <br />
                    <span className="text-xs">This logo(s) will be used on the IFTA 2025 digital and print collateral. Logo should be of very high quality and in vector format.</span>
                  </Label>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">
                      <strong>Supported formats:</strong> PNG, JPG, JPEG, AI, SVG, EPS, PDF, ZIP
                      <br />
                      <strong>Max file size:</strong> 10MB per file
                      <br />
                      <strong className="underline">Note: Upload high-resolution logos (vector formats preferred) for print and digital use.</strong>
                    </p>
                  </div>

                  {/* Logo Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Display existing logos */}
                    {logoVariants.map((logo, index) => {
                      const isImageFile = !logo.fileType || logo.fileType.startsWith('image/') ||
                                         (logo.isExisting && logo.url);
                      const getFileExtension = () => {
                        if (logo.fileName) {
                          return logo.fileName.split('.').pop().toUpperCase()
                        }
                        if (logo.fileType) {
                          if (logo.fileType.includes('pdf')) return 'PDF'
                          if (logo.fileType.includes('zip')) return 'ZIP'
                          if (logo.fileType.includes('postscript')) return 'AI/EPS'
                          if (logo.fileType.includes('svg')) return 'SVG'
                        }
                        return 'FILE'
                      }

                      return (
                        <div
                          key={index}
                          className="relative border-2 border-gray-200 rounded-lg p-3 bg-white"
                        >
                          {isImageFile ? (
                            <img
                              src={logo.url || (logo.file ? URL.createObjectURL(logo.file) : '')}
                              alt={logo.name}
                              className="w-full h-24 object-contain mb-2"
                            />
                          ) : (
                            <div className="w-full h-24 flex flex-col items-center justify-center bg-gray-50 rounded mb-2">
                              <div className="text-2xl font-bold text-gray-600">{getFileExtension()}</div>
                              <div className="text-xs text-gray-500 mt-1 truncate max-w-full px-2" title={logo.fileName}>
                                {logo.fileName || 'Logo file'}
                              </div>
                            </div>
                          )}
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-center">
                              Logo {index + 1}
                            </div>
                            <div className="flex gap-1">
                            <Label htmlFor={`edit-logo-input-${index}`} className="flex-1">
                              <Input
                                id={`edit-logo-input-${index}`}
                                key={`edit-logo-${index}`}
                                type="file"
                                accept="image/*,.ai,.svg,.eps,.pdf,.zip,application/postscript,application/pdf,application/zip,image/svg+xml"
                                onChange={(e) => handleLogoSelect(e, index)}
                                className="hidden"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                className="w-full h-7 text-xs flex items-center justify-center gap-1"
                                onClick={(e) => {
                                  e.preventDefault();
                                  document.getElementById(`edit-logo-input-${index}`).click();
                                }}
                              >
                                <Edit2 className="h-3 w-3" />
                                <span>Edit</span>
                              </Button>
                            </Label>
                            <Button
                              variant="outline"
                              size="sm"
                              type="button"
                              onClick={() => handleRemoveLogo(index)}
                              className="h-7 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50 flex items-center gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              <span>Delete</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    )
                    })}

                    {/* Add new logo button */}
                    {logoVariants.length < 5 && (
                      <Label className="cursor-pointer">
                        <Input
                          key={`add-logo-${logoVariants.length}`}
                          type="file"
                          accept="image/*,.ai,.svg,.eps,.pdf,.zip,application/postscript,application/pdf,application/zip,image/svg+xml"
                          onChange={handleLogoSelect}
                          className="hidden"
                        />
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 h-full min-h-[150px] flex flex-col items-center justify-center hover:border-primary hover:bg-gray-50 transition-colors">
                          <Plus className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-xs text-gray-600 font-medium">Add Logo</span>
                          <span className="text-xs text-gray-400">Click to upload</span>
                        </div>
                      </Label>
                    )}
                  </div>

                  {logoVariants.length === 0 && (
                    <p className="text-sm text-red-500 text-center">
                      At least one logo is required
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with buttons - sticky at bottom */}
        <div className="flex gap-3 px-6 py-4 border-t bg-white sticky bottom-0">
          <Button
            onClick={handleSubmit}
            className="flex-1 h-11"
            disabled={
              loading ||
              fetchingData ||
              !companyDetails.companyWebsite ||
              !companyDetails.companyDescription ||
              (!isUpdateMode && logoVariants.length === 0 && !existingPhotos.logoPath)
            }
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {loadingMessage || 'Processing...'}
              </div>
            ) : (
              isUpdateMode ? 'Update Profile' : 'Complete Profile'
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleClose}
            className="h-11"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Image Crop Modal for Company Logo */}
    <ResizableCropModal
      open={logoCropModalOpen}
      onClose={() => setLogoCropModalOpen(false)}
      imageSrc={logoTempImageSrc}
      onCropComplete={handleLogoCropComplete}
      title="Crop Company Logo"
    />
    </>
  )
}